// Bridge between the React Native WebView host and the dashboard's AuthContext.
//
// In the mobile app, the WebView seeds localStorage with a direct_auth session
// and sets `window.__DONEVERSE_MOBILE_BRIDGE__ = true` before this script loads.
// When the dashboard would normally refresh the access token via Auth0, we
// instead postMessage the request to the native shell, which owns refresh and
// pushes the new session back via `window.__doneverseMobileBridge.acceptSession`.

export interface DirectAuthSessionPayload {
  accessToken: string
  idToken?: string
  refreshToken?: string
  expiresAt: number
  user: {
    id: string
    sub: string
    email?: string
    name?: string
    picture?: string
    email_verified?: boolean
    updated_at?: string
  }
}

type Pending = {
  resolve: (session: DirectAuthSessionPayload) => void
  reject: (error: Error) => void
}

type ReactNativeWebViewBridge = {
  postMessage: (data: string) => void
}

type MobileBridgeWindow = Window & {
  ReactNativeWebView?: ReactNativeWebViewBridge
  __DONEVERSE_MOBILE_BRIDGE__?: boolean
  __DONEVERSE_NATIVE_CALLS__?: boolean
  __doneverseMobileBridge?: {
    acceptSession: (session: DirectAuthSessionPayload, requestId: string | null) => void
    rejectSession: (requestId: string | null) => void
  }
}

const pending = new Map<string, Pending>()
let acceptHandler: ((session: DirectAuthSessionPayload) => void) | null = null

function bridgeWindow(): MobileBridgeWindow | null {
  if (typeof window === 'undefined') return null
  return window as MobileBridgeWindow
}

export function isMobileBridge(): boolean {
  const w = bridgeWindow()
  return !!(w && w.__DONEVERSE_MOBILE_BRIDGE__ && w.ReactNativeWebView)
}

export function hasNativeCalls(): boolean {
  const w = bridgeWindow()
  return !!(w && w.__DONEVERSE_NATIVE_CALLS__ && w.ReactNativeWebView)
}

export function postNativeCallStart(contactId: string, contactName: string): boolean {
  const w = bridgeWindow()
  if (!w || !w.__DONEVERSE_NATIVE_CALLS__ || !w.ReactNativeWebView) return false
  try {
    document.dispatchEvent(new CustomEvent('doneverse:native-call-starting'))
    w.ReactNativeWebView.postMessage(
      JSON.stringify({ type: 'call:start', contact_id: contactId, contact_name: contactName }),
    )
    return true
  } catch {
    return false
  }
}

function ensureGlobalBridge(w: MobileBridgeWindow) {
  if (w.__doneverseMobileBridge) return
  w.__doneverseMobileBridge = {
    acceptSession: (session, requestId) => {
      acceptHandler?.(session)
      if (requestId) {
        const slot = pending.get(requestId)
        if (slot) {
          pending.delete(requestId)
          slot.resolve(session)
        }
      } else {
        // Broadcast accept (no requestId) — resolve any in-flight refresh with
        // the latest session so callers aren't left hanging.
        for (const [id, slot] of pending) {
          pending.delete(id)
          slot.resolve(session)
        }
      }
    },
    rejectSession: (requestId) => {
      const error = new Error('Mobile bridge refresh failed')
      if (requestId) {
        const slot = pending.get(requestId)
        if (slot) {
          pending.delete(requestId)
          slot.reject(error)
        }
        return
      }
      for (const [id, slot] of pending) {
        pending.delete(id)
        slot.reject(error)
      }
    },
  }
}

export function setMobileBridgeAcceptHandler(
  handler: ((session: DirectAuthSessionPayload) => void) | null,
): void {
  const w = bridgeWindow()
  if (!w) return
  ensureGlobalBridge(w)
  acceptHandler = handler
}

export function requestMobileBridgeRefresh(): Promise<DirectAuthSessionPayload> {
  const w = bridgeWindow()
  if (!w || !w.ReactNativeWebView) {
    return Promise.reject(new Error('Mobile bridge unavailable'))
  }
  ensureGlobalBridge(w)
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return new Promise<DirectAuthSessionPayload>((resolve, reject) => {
    pending.set(id, { resolve, reject })
    try {
      w.ReactNativeWebView!.postMessage(JSON.stringify({ type: 'auth:refresh-request', id }))
    } catch (error) {
      pending.delete(id)
      reject(error instanceof Error ? error : new Error('Failed to message mobile shell'))
    }
  })
}

/**
 * Tell the native shell whether a call is live so it can hold a screen wake
 * lock for the duration — the page itself can't stop the phone from locking,
 * and locking suspends the WebView's WebRTC connection mid-call.
 */
export function postMobileBridgeCallActive(active: boolean): void {
  const w = bridgeWindow()
  if (!w || !w.ReactNativeWebView) return
  try {
    w.ReactNativeWebView.postMessage(JSON.stringify({ type: 'call:active', active }))
  } catch {
    // ignore — worst case the screen dims during the call
  }
}

export function postMobileBridgeSignOut(): void {
  const w = bridgeWindow()
  if (!w || !w.ReactNativeWebView) return
  try {
    w.ReactNativeWebView.postMessage(JSON.stringify({ type: 'auth:signout' }))
  } catch {
    // ignore — the native shell will sign out on its own next interaction
  }
}
